import React, { ReactNode, useMemo, ReactElement } from 'react'
import { Link } from 'react-router'
import cx from 'classnames'
import './style.sass'
import { Icon, type IconNamesEnum } from 'common/Icon'

type LocationShape = {
	pathname: string
	search: string
	hash: string
	state?: any
}
const getContent = (children: ReactNode, iconBefore?: IconNamesEnum, iconAfter?: IconNamesEnum, stroke?: string) => {
	if (iconBefore || iconAfter) {
		return (
			<span className='Button__content'>
				{iconBefore && <Icon className='Button__icon--before' name={iconBefore} width={16} height={16} stroke={stroke} />}
				{children}
				{iconAfter && <Icon className='Button__icon--after' name={iconAfter} width={16} height={16} stroke={stroke} />}
			</span>
		)
	}

	return children
}

type ButtonProps = {
	className?: string
	regular?: boolean
	outline?: boolean
	dark?: boolean
	grey?: boolean
	fit?: boolean
	green?: boolean
	blueBorder?: boolean
	greyBorder?: boolean
	to?: string
	disabled?: boolean
	href?: string
	withSiblings?: boolean
	iconBefore?: IconNamesEnum
	iconAfter?: IconNamesEnum
	stroke?: string
	children: ReactNode
	[key: string]: any
}

type ButtonType =
	| {
			variant: 'button'
	  }
	| {
			variant: 'routerLink'
			to: string | LocationShape
	  }
	| {
			variant: 'externalLink'
			href: string
	  }

type IPropType =
	| ReactElement<'a'>
	| ReactElement<'button'>
	| ReactElement<
			React.ComponentType<{
				children?: React.ReactNode
				className?: string
				replace?: boolean
				to: string | LocationShape
			}>
	  >

export const Button = ({
	children,
	className,
	regular,
	outline,
	dark,
	grey,
	fit,
	green,
	blueBorder,
	greyBorder,
	redBorder,
	disabled,
	withSiblings,
	iconBefore,
	iconAfter,
	stroke = '#ffffff',
	...props
}: ButtonProps): IPropType => {
	const componentType: ButtonType = useMemo(() => {
		if (props.to) {
			const link: string | LocationShape = props.to
			return { variant: 'routerLink', to: link }
		}
		if (props.href) return { variant: 'externalLink', href: props.href }

		return { variant: 'button' }
	}, [props.href, props.to])

	const cs = cx('Button', className, {
		'Button--regular': regular,
		'Button--outline': outline,
		'Button--dark': dark,
		'Button--grey': grey,
		'Button--green': green,
		'Button--red-border': redBorder,
		'Button--disabled': disabled,
		'Button--with-siblings': withSiblings,
		'Button--link': props.href || props.to,
		'Button--with-icon': iconBefore || iconAfter,
		'Button--blue-border': blueBorder,
		'Button--grey-border': greyBorder,
		'Button--fit': fit,
	})
	if (componentType.variant === 'routerLink')
		return (
			<Link to={componentType.to} className={cs} {...props}>
				{getContent(children, iconBefore, iconAfter, stroke)}
			</Link>
		)
	if (componentType.variant === 'externalLink')
		return (
			<a href={componentType.href} className={cs} {...props}>
				{getContent(children, iconBefore, iconAfter, stroke)}
			</a>
		)
	return (
		<button {...props} className={cs} disabled={disabled}>
			{getContent(children, iconBefore, iconAfter, stroke)}
		</button>
	)
}

export default Button
